import { getOntologyApiBaseUrl } from '../../config'
import { consumeSSE } from '../../agents/sse-client'

const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

export type GraphChangeStreamStatus = 'connected' | 'disconnected'
export type GraphChangeStreamEventType = 'ready' | 'signal' | 'heartbeat'

export interface SubscribeGraphChangeSignalsOptions {
  onSignal: () => void
  onStatus: (status: GraphChangeStreamStatus) => void
  signal: AbortSignal
}

export function nextGraphSignalBackoffMs(attempt: number): number {
  return Math.min(INITIAL_BACKOFF_MS * 2 ** Math.max(0, attempt), MAX_BACKOFF_MS)
}

export function graphChangeStreamEventType(event: unknown): GraphChangeStreamEventType | undefined {
  if (!event || typeof event !== 'object') return undefined
  const type = (event as { type?: unknown }).type
  return type === 'ready' || type === 'signal' || type === 'heartbeat' ? type : undefined
}

export function isGraphChangeStreamUnavailable(error: unknown): boolean {
  return error instanceof Error && error.message.includes('HTTP 503')
}

/** Reconnecting signal stream; the change-token poll remains the recovery path. */
export async function subscribeGraphChangeSignals({
  onSignal,
  onStatus,
  signal
}: SubscribeGraphChangeSignalsOptions): Promise<void> {
  let attempt = 0
  while (!signal.aborted) {
    let connected = false
    let failure: unknown
    try {
      await consumeSSE<unknown>({
        url: `${getOntologyApiBaseUrl()}/graph/change-stream`,
        body: {},
        signal,
        onEvent: (event) => {
          if (signal.aborted) return
          const type = graphChangeStreamEventType(event)
          if (type === 'ready') {
            connected = true
            attempt = 0
            onStatus('connected')
          } else if (type === 'signal') {
            onSignal()
          }
        }
      })
    } catch (error) {
      failure = error
    }
    if (signal.aborted) return

    onStatus('disconnected')
    const delayMs = isGraphChangeStreamUnavailable(failure)
      ? MAX_BACKOFF_MS
      : nextGraphSignalBackoffMs(attempt)
    if (!connected) attempt += 1
    await abortableDelay(delayMs, signal)
  }
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true }
    )
  })
}
